import { useLanguage } from "@tlc/ls2pac/components/Language";
import { useLSSearch } from "./LSSearch";
import {
  Chip,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import React from "react";
import { SearchSortType } from "../../account/lists/SavedListSortControl";
import SortIcon from "@mui/icons-material/Sort";
import { useIsMobile } from "@tlc/ui/useIsMobile";
import { useReservesSearch } from "../Reserves/ReservesSearchProvider";
import { ReservesSearchSortType } from "../Reserves/ReservesSearchSortType";

interface ResultsSortControlProps<T extends string> {
  options: Record<T, string>;
  sort: T;
  loadingResults: boolean;
  totalHits: number;
  setSort: (sort: T) => void;
  id: string;
}
export function ResultsSortControl<T extends string>({
  sort,
  loadingResults,
  totalHits,
  setSort,
  options,
  id,
}: ResultsSortControlProps<T>) {
  const isMobile = useIsMobile();
  const { translate } = useLanguage();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickChip = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  if (isMobile) {
    return (
      <>
        <Chip
          variant="outlined"
          label={options[sort]}
          icon={<SortIcon />}
          color="primary"
          aria-haspopup="listbox"
          aria-controls={`${id}-menu`}
          id={`${id}-chip`}
          aria-expanded={open ? "true" : undefined}
          onClick={handleClickChip}
        />
        <Menu
          id={`${id}-menu`}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": `${id}-chip`,
            role: "listbox",
          }}
        >
          {Object.entries(options).map(([value, label]) => (
            <MenuItem
              key={value}
              value={value}
              selected={value === sort}
              onClick={() => {
                setSort(value as T);
                handleClose();
              }}
            >
              {label as string}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <FormControl>
      <InputLabel id={`${id}-label`}>{translate("sortBy")}</InputLabel>
      <Select
        size="small"
        labelId={`${id}-label`}
        label={translate("sortBy")}
        disabled={loadingResults || totalHits === 0}
        value={sort}
        onChange={(event: SelectChangeEvent<T>) => {
          setSort(event.target.value as T);
        }}
        inputProps={{
          "aria-labelledby": `${id}-label`,
        }}
      >
        {Object.entries(options).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label as string}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export const LSReservesSortControl = () => {
  const { translate } = useLanguage();
  const {
    searchParams: { sort },
    loadingResults,
    totalHits,
    setSort,
  } = useReservesSearch();

  const options: Record<ReservesSearchSortType, string> = {
    [ReservesSearchSortType.Title]: translate("title"),
    [ReservesSearchSortType.Author]: translate("author"),
  };

  return (
    <ResultsSortControl<ReservesSearchSortType>
      options={options}
      sort={sort}
      loadingResults={loadingResults}
      totalHits={totalHits}
      setSort={setSort}
      id={"ls-reserves-sort"}
    />
  );
};

export const LSSearchSortControl = () => {
  const { translate } = useLanguage();
  const {
    searchParams: { sort },
    loadingResults,
    totalHits,
    setSort,
  } = useLSSearch();

  const options: Record<SearchSortType, string> = {
    [SearchSortType.Relevancy]: translate("Relevancy"),
    [SearchSortType.NewlyAdded]: translate("NewlyAdded"),
    [SearchSortType.PubDate]: translate("PubDate"),
    [SearchSortType.Rating]: translate("Rating"),
    [SearchSortType.Title]: translate("Title"),
    [SearchSortType.Author]: translate("Author"),
    [SearchSortType.CallNumber]: translate("CallNumber"),
  };

  return (
    <ResultsSortControl<SearchSortType>
      options={options}
      sort={sort}
      loadingResults={loadingResults}
      totalHits={totalHits}
      setSort={setSort}
      id={"ls-search-sort"}
    />
  );
};
